import React, { useId } from 'react';

export interface PlasticEnclosureSvgProps {
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
  /** Gerçek genişlik (mm) - Varsayılan: 400 mm */
  widthMm?: number;
  /** Gerçek yükseklik (mm) - Varsayılan: 600 mm */
  heightMm?: number;
  /** Gerçek derinlik (mm) - Varsayılan: 160 mm */
  depthMm?: number;
  /** Gerçek ölçü cetvellerini ve teknik ölçek etiketini göster */
  showDimensions?: boolean;
  /** DIN rayı kılavuz çizgilerini göster */
  showRailGuides?: boolean;
  /** Gövde plastik rengi (varsayılan: açık endüstriyel gri) */
  bodyColor?: string;
  /** Taban sacı rengi (varsayılan: koyu antrasit) */
  backplateColor?: string;
  title?: string;
}

/**
 * PlasticEnclosureSvg
 * Gerçek ölçülü (400x600x160mm) endüstriyel plastik otomat/kumanda panosu.
 * Özellikler:
 * - Kenar ve köşelerdeki vidalar kaldırılmış, temiz enjeksiyon kalıp gövde.
 * - DIN rayları altındaki montaj ayakları kaldırılmış, kesintisiz 35mm DIN EN 50022 delikli galvaniz çelik ray.
 * - Bakır vidalı klemens dizisi şeklinde Nötr ve Toprak baraları (Mavi ve Sarı/Yeşil izolatörlü).
 * - Gerçek ölçü (mm) parametreleri ve teknik CAD boyutlandırma desteği.
 */
export const PlasticEnclosureSvg: React.FC<PlasticEnclosureSvgProps> = ({
  width = '100%',
  height = '100%',
  className = '',
  style,
  widthMm = 400,
  heightMm = 600,
  depthMm = 160,
  showDimensions = true,
  showRailGuides = false,
  bodyColor,
  backplateColor,
  title = 'Plastik Elektrik Kumanda Panosu (400x600 mm)',
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, '_');

  const basePlastic = bodyColor || '#e6e9ee';
  const baseBackplate = backplateColor || '#475569';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 840"
      width={width}
      height={height}
      className={`plastic-enclosure-svg ${className}`}
      style={{
        display: 'block',
        userSelect: 'none',
        overflow: 'visible',
        ...style,
      }}
      role="img"
      aria-label={title}
      data-testid="plastic-enclosure"
    >
      <title>{title}</title>

      <defs>
        {/* --- GÖLGE FİLTRELERİ --- */}
        <filter id={`enclosureShadow-${uid}`} x="-8%" y="-8%" width="120%" height="120%">
          <feDropShadow dx="0" dy="16" stdDeviation="16" floodColor="#090d14" floodOpacity="0.45" />
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#090d14" floodOpacity="0.3" />
        </filter>

        <filter id={`cavityDepth-${uid}`} x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#0d131d" floodOpacity="0.65" />
          <feDropShadow dx="6" dy="0" stdDeviation="8" floodColor="#0d131d" floodOpacity="0.4" />
        </filter>

        <filter id={`railDropShadow-${uid}`} x="-5%" y="-15%" width="110%" height="150%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#070b12" floodOpacity="0.45" />
        </filter>

        <filter id={`busbarDropShadow-${uid}`} x="-10%" y="-20%" width="120%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#05080f" floodOpacity="0.5" />
        </filter>

        {/* --- DEGRADELER --- */}
        {/* Dış Çerçeve Işıklandırması */}
        <linearGradient id={`outerRimGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="15%" stopColor={basePlastic} />
          <stop offset="70%" stopColor="#cfd5df" />
          <stop offset="100%" stopColor="#9da6b5" />
        </linearGradient>

        {/* Dış Gövde */}
        <linearGradient id={`outerBodyGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="40%" stopColor={basePlastic} />
          <stop offset="100%" stopColor="#cbd5e1" />
        </linearGradient>

        {/* Conta Kanalı */}
        <linearGradient id={`gasketGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>

        {/* İç Duvar Yükseltisi */}
        <linearGradient id={`innerWallTopGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="30%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#94a3b8" />
        </linearGradient>

        {/* Taban Sacı */}
        <linearGradient id={`backplateGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#64748b" />
          <stop offset="40%" stopColor={baseBackplate} />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* 35mm Galvanizli Çelik DIN Rayı Degradesi (DIN EN 50022) */}
        <linearGradient id={`dinRailSteelGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="8%" stopColor="#cbd5e1" />
          <stop offset="20%" stopColor="#94a3b8" />
          <stop offset="35%" stopColor="#64748b" />
          <stop offset="65%" stopColor="#475569" />
          <stop offset="80%" stopColor="#64748b" />
          <stop offset="92%" stopColor="#cbd5e1" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* DIN Rayı Oluk/Yuva İç Gölgesi */}
        <linearGradient id={`dinSlotShadow-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Som Bakır Bara Degradesi (Copper Busbar) */}
        <linearGradient id={`copperBarGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fef08a" />
          <stop offset="15%" stopColor="#f59e0b" />
          <stop offset="45%" stopColor="#d97706" />
          <stop offset="80%" stopColor="#b45309" />
          <stop offset="100%" stopColor="#78350f" />
        </linearGradient>

        {/* Pirinç/Çelik Klemens Vidası Degradesi */}
        <radialGradient id={`brassScrewGrad-${uid}`} cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#fef9c3" />
          <stop offset="30%" stopColor="#fde047" />
          <stop offset="65%" stopColor="#ca8a04" />
          <stop offset="90%" stopColor="#854d0e" />
          <stop offset="100%" stopColor="#532d07" />
        </radialGradient>

        {/* Mavi Nötr İzolatör Ayağı */}
        <linearGradient id={`blueInsulatorGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="25%" stopColor="#0284c7" />
          <stop offset="85%" stopColor="#0369a1" />
          <stop offset="100%" stopColor="#0c4a6e" />
        </linearGradient>

        {/* Sarı/Yeşil Toprak İzolatör Ayağı */}
        <linearGradient id={`greenInsulatorGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#bef264" />
          <stop offset="30%" stopColor="#65a30d" />
          <stop offset="80%" stopColor="#4d7c0f" />
          <stop offset="100%" stopColor="#1a2e05" />
        </linearGradient>

        {/* Taban Sacı Teknik Hizalama Kılavuz Deseni */}
        <pattern id={`backplateGrid-${uid}`} width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="10" cy="10" r="0.75" fill="rgba(255, 255, 255, 0.1)" />
          <path d="M 10 0 L 10 2 M 10 18 L 10 20 M 0 10 L 2 10 M 18 10 L 20 10" stroke="rgba(255, 255, 255, 0.04)" strokeWidth="0.8" />
        </pattern>
      </defs>

      {/* ============================================================ */}
      {/* 1. DIŞ KASA VE GÖVDE (VİDASIZ, PÜRÜZSÜZ ENJEKSİYON KALIP)     */}
      {/* ============================================================ */}
      <g id="enclosure-outer-housing" data-testid="enclosure-outer-housing" filter={`url(#enclosureShadow-${uid})`}>
        {/* Dış Gövde Ana Kabuğu (Köşe vidaları kaldırılmış, temiz polikarbonat gövde) */}
        <rect
          x="20"
          y="20"
          width="600"
          height="800"
          rx="26"
          ry="26"
          fill={`url(#outerBodyGrad-${uid})`}
          stroke="#94a3b8"
          strokeWidth="1.5"
        />

        {/* Dış Eğim ve Işık Yansıması */}
        <rect
          x="23"
          y="23"
          width="594"
          height="794"
          rx="23"
          ry="23"
          fill="none"
          stroke={`url(#outerRimGrad-${uid})`}
          strokeWidth="3.2"
        />

        {/* Çevre Conta Kanalı (IP65 Sızdırmazlık Fitili) */}
        <rect
          x="35"
          y="35"
          width="570"
          height="770"
          rx="18"
          ry="18"
          fill="none"
          stroke={`url(#gasketGrad-${uid})`}
          strokeWidth="2.8"
        />
        <rect
          x="37"
          y="37"
          width="566"
          height="766"
          rx="16"
          ry="16"
          fill="none"
          stroke="#1e293b"
          strokeWidth="1"
          strokeOpacity="0.6"
        />

        {/* İç Yükseltilmiş Çerçeve Kenarlığı */}
        <rect
          x="44"
          y="44"
          width="552"
          height="752"
          rx="14"
          ry="14"
          fill={`url(#innerWallTopGrad-${uid})`}
          stroke="#64748b"
          strokeWidth="1.2"
        />

        {/* Işık Vurguları */}
        <path
          d="M 64 45 L 576 45 A 14 14 0 0 1 595 64"
          fill="none"
          stroke="#ffffff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeOpacity="0.8"
        />
        <path
          d="M 45 64 L 45 776 A 14 14 0 0 0 64 795"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeOpacity="0.6"
        />
      </g>

      {/* ============================================================ */}
      {/* 2. İÇ BOŞLUK VE TABAN MONTAJ SACI (BACKPLATE)                 */}
      {/* ============================================================ */}
      <g id="enclosure-interior-cavity" data-testid="enclosure-interior-cavity">
        {/* Taban Sacı Yüzeyi */}
        <rect
          x="54"
          y="54"
          width="532"
          height="732"
          rx="10"
          ry="10"
          fill={`url(#backplateGrad-${uid})`}
        />

        {/* İç Duvar Derinlik Gölgesi */}
        <rect
          x="54"
          y="54"
          width="532"
          height="732"
          rx="10"
          ry="10"
          fill="none"
          stroke="#0f172a"
          strokeWidth="8"
          strokeOpacity="0.45"
          filter={`url(#cavityDepth-${uid})`}
        />

        {/* Milimetrik Hizalama Izgarası */}
        <rect
          x="56"
          y="56"
          width="528"
          height="728"
          rx="8"
          ry="8"
          fill={`url(#backplateGrid-${uid})`}
        />

        {/* Kalıp İtici Pin İzleri */}
        <g id="ejector-pin-marks" opacity="0.25">
          {[
            { cx: 160, cy: 150 },
            { cx: 480, cy: 150 },
            { cx: 160, cy: 380 },
            { cx: 480, cy: 380 },
            { cx: 160, cy: 620 },
            { cx: 480, cy: 620 },
          ].map((pt, idx) => (
            <g key={`ejector-${idx}`}>
              <circle cx={pt.cx} cy={pt.cy} r="8" fill="none" stroke="#1e293b" strokeWidth="1" />
              <circle cx={pt.cx} cy={pt.cy} r="6.5" fill="none" stroke="#ffffff" strokeWidth="0.6" opacity="0.4" />
            </g>
          ))}
        </g>

        {/* Pano Teknik Bilgi Etiketi (Gerçek Ölçülerle) */}
        <g id="molded-specs-text" fill="rgba(255,255,255,0.22)" fontSize="8.5" fontFamily="system-ui, sans-serif" fontWeight="bold">
          <g transform="translate(70, 764)">
            <rect x="0" y="0" width="10" height="10" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="1" />
            <rect x="2.5" y="2.5" width="5" height="5" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8" />
          </g>
          <text x="88" y="772">IP65</text>
          <text x="120" y="772">IK08</text>
          <text x="156" y="772">EN 62208 / IEC 60670</text>
          <text x="282" y="772">BOYUT: {widthMm} x {heightMm} x {depthMm} mm</text>
          <text x="470" y="772">MAX 63A 400V~</text>
        </g>
      </g>

      {/* ============================================================ */}
      {/* 3. GERÇEK ÖLÇÜ CAD BOYUTLANDIRMA ÇİZGİLERİ (İSTEĞE BAĞLI)    */}
      {/* ============================================================ */}
      {showDimensions && (
        <g id="cad-dimension-lines" opacity="0.75" pointerEvents="none">
          {/* Üst Genişlik Ölçü Çizgisi: 400 mm */}
          <g transform="translate(0, -6)">
            {/* Sol uzatma çizgisi */}
            <line x1="20" y1="20" x2="20" y2="8" stroke="#38bdf8" strokeWidth="1" strokeDasharray="3,2" />
            {/* Sağ uzatma çizgisi */}
            <line x1="620" y1="20" x2="620" y2="8" stroke="#38bdf8" strokeWidth="1" strokeDasharray="3,2" />
            {/* Ana ölçü çizgisi */}
            <line x1="26" y1="12" x2="614" y2="12" stroke="#38bdf8" strokeWidth="1.2" />
            {/* Sol ve sağ ok başları */}
            <path d="M 22 12 L 28 9.5 L 28 14.5 Z" fill="#38bdf8" />
            <path d="M 618 12 L 612 9.5 L 612 14.5 Z" fill="#38bdf8" />
            {/* Ölçü etiketi arka planı ve metni */}
            <rect x="275" y="4" width="90" height="16" rx="3" fill="#090d16" stroke="#0284c7" strokeWidth="0.8" />
            <text x="320" y="15.5" fill="#38bdf8" fontSize="9.5" fontWeight="bold" fontFamily="monospace" textAnchor="middle">
              {widthMm} mm
            </text>
          </g>

          {/* Sol Yükseklik Ölçü Çizgisi: 600 mm */}
          <g transform="translate(-6, 0)">
            <line x1="20" y1="20" x2="8" y2="20" stroke="#38bdf8" strokeWidth="1" strokeDasharray="3,2" />
            <line x1="20" y1="820" x2="8" y2="820" stroke="#38bdf8" strokeWidth="1" strokeDasharray="3,2" />
            <line x1="12" y1="26" x2="12" y2="814" stroke="#38bdf8" strokeWidth="1.2" />
            <path d="M 12 22 L 9.5 28 L 14.5 28 Z" fill="#38bdf8" />
            <path d="M 12 818 L 9.5 812 L 14.5 812 Z" fill="#38bdf8" />
            <g transform="translate(12, 420) rotate(-90)">
              <rect x="-45" y="-8" width="90" height="16" rx="3" fill="#090d16" stroke="#0284c7" strokeWidth="0.8" />
              <text x="0" y="3.5" fill="#38bdf8" fontSize="9.5" fontWeight="bold" fontFamily="monospace" textAnchor="middle">
                {heightMm} mm
              </text>
            </g>
          </g>
        </g>
      )}

      {/* ============================================================ */}
      {/* 4. KESİNTİSİZ 35MM GALVANİZLİ ÇELİK DIN RAYLARI (AYAKSIZ)      */}
      {/* ============================================================ */}
      <g id="slotted-din-rails" data-testid="slotted-din-rails">
        {/* --- 1. ÜST DIN RAYI (Y = 240, 35mm Standart DIN EN 50022) --- */}
        <g id="upper-din-rail-assembly" transform="translate(68, 222.5)" filter={`url(#railDropShadow-${uid})`}>
          {/* Ray Arka Gölgesi */}
          <rect x="0" y="0" width="504" height="35" rx="2" fill="#0f172a" opacity="0.6" />

          {/* Ray Galvanizli Çelik Gövde */}
          <rect x="0" y="0" width="504" height="35" rx="1.5" fill={`url(#dinRailSteelGrad-${uid})`} stroke="#475569" strokeWidth="1" />

          {/* Üst ve Alt Büküm Flanşı Işık Şeritleri */}
          <line x1="0" y1="1" x2="504" y2="1" stroke="#ffffff" strokeWidth="1.2" opacity="0.8" />
          <line x1="0" y1="7.5" x2="504" y2="7.5" stroke="#334155" strokeWidth="1" />
          <line x1="0" y1="27.5" x2="504" y2="27.5" stroke="#ffffff" strokeWidth="0.8" opacity="0.5" />
          <line x1="0" y1="34" x2="504" y2="34" stroke="#1e293b" strokeWidth="1.2" />

          {/* Orta Oluk Kanalı (Recessed Trough, 20mm derinlik hissi) */}
          <rect x="0" y="8" width="504" height="19" fill="#475569" stroke="#334155" strokeWidth="0.8" />
          <rect x="0" y="9" width="504" height="17" fill="#334155" />

          {/* Standart DIN 35 Delikli Oluk Yuvaları (25 x 5.2 mm Slotted Perforations) */}
          {Array.from({ length: 13 }).map((_, i) => {
            const slotX = 14 + i * 38;
            return (
              <g key={`upper-slot-${i}`}>
                {/* Delikten arkadaki taban sacının görünmesi */}
                <rect x={slotX} y="13.5" width="25" height="8" rx="4" fill="#1e293b" stroke="#0f172a" strokeWidth="1" />
                <rect x={slotX + 1} y="14.5" width="23" height="6" rx="3" fill="#111827" />
                {/* Delik kenarı metalik pah kırma yansıması */}
                <path d={`M ${slotX + 4} 21.5 L ${slotX + 21} 21.5`} stroke="#64748b" strokeWidth="0.8" />
              </g>
            );
          })}
        </g>

        {/* --- 2. ALT DIN RAYI (Y = 520, 35mm Standart DIN EN 50022) --- */}
        <g id="lower-din-rail-assembly" transform="translate(68, 502.5)" filter={`url(#railDropShadow-${uid})`}>
          <rect x="0" y="0" width="504" height="35" rx="2" fill="#0f172a" opacity="0.6" />
          <rect x="0" y="0" width="504" height="35" rx="1.5" fill={`url(#dinRailSteelGrad-${uid})`} stroke="#475569" strokeWidth="1" />
          <line x1="0" y1="1" x2="504" y2="1" stroke="#ffffff" strokeWidth="1.2" opacity="0.8" />
          <line x1="0" y1="7.5" x2="504" y2="7.5" stroke="#334155" strokeWidth="1" />
          <line x1="0" y1="27.5" x2="504" y2="27.5" stroke="#ffffff" strokeWidth="0.8" opacity="0.5" />
          <line x1="0" y1="34" x2="504" y2="34" stroke="#1e293b" strokeWidth="1.2" />

          <rect x="0" y="8" width="504" height="19" fill="#475569" stroke="#334155" strokeWidth="0.8" />
          <rect x="0" y="9" width="504" height="17" fill="#334155" />

          {Array.from({ length: 13 }).map((_, i) => {
            const slotX = 14 + i * 38;
            return (
              <g key={`lower-slot-${i}`}>
                <rect x={slotX} y="13.5" width="25" height="8" rx="4" fill="#1e293b" stroke="#0f172a" strokeWidth="1" />
                <rect x={slotX + 1} y="14.5" width="23" height="6" rx="3" fill="#111827" />
                <path d={`M ${slotX + 4} 21.5 L ${slotX + 21} 21.5`} stroke="#64748b" strokeWidth="0.8" />
              </g>
            );
          })}
        </g>

        {showRailGuides && (
          <g id="rail-technical-guides" opacity="0.45" pointerEvents="none">
            <line x1="60" y1="240" x2="580" y2="240" stroke="#38bdf8" strokeWidth="0.8" strokeDasharray="4,3" />
            <line x1="60" y1="520" x2="580" y2="520" stroke="#38bdf8" strokeWidth="0.8" strokeDasharray="4,3" />
          </g>
        )}
      </g>

      {/* ============================================================ */}
      {/* 5. BAKIR VİDALI KLEMENS DİZİSİ (NÖTR VE TOPRAK BARALARI)       */}
      {/* ============================================================ */}
      <g id="copper-screw-busbars" data-testid="copper-screw-busbars" filter={`url(#busbarDropShadow-${uid})`}>
        {/* --- NÖTR BARASI (NEUTRAL BUSBAR - MAVİ İZOLATÖRLÜ BAKIR DİZİ) --- */}
        {/* Konum: Üst Sol-Orta (X = 180, Y = 74) */}
        <g id="neutral-busbar-unit" transform="translate(195, 74)">
          {/* Mavi Yan İzolatör Ayakları */}
          <rect x="-8" y="2" width="16" height="26" rx="3" fill={`url(#blueInsulatorGrad-${uid})`} stroke="#0369a1" strokeWidth="1" />
          <rect x="156" y="2" width="16" height="26" rx="3" fill={`url(#blueInsulatorGrad-${uid})`} stroke="#0369a1" strokeWidth="1" />

          {/* Som Bakır Bara Gövdesi (Solid Extruded Copper Bar) */}
          <rect x="4" y="5" width="156" height="20" rx="2" fill={`url(#copperBarGrad-${uid})`} stroke="#92400e" strokeWidth="1.2" />
          {/* Bakır Üst Eğim Işık Yansıması */}
          <line x1="6" y1="6.5" x2="158" y2="6.5" stroke="#fef9c3" strokeWidth="1" opacity="0.85" />
          <line x1="6" y1="23.5" x2="158" y2="23.5" stroke="#451a03" strokeWidth="0.8" />

          {/* Sol 'N' ve Mavi Tanım Rozeti */}
          <rect x="8" y="7" width="16" height="16" rx="2" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" />
          <text x="16" y="19" fill="#ffffff" fontSize="9" fontWeight="bold" fontFamily="monospace" textAnchor="middle">N</text>

          {/* 6 Adet Vidalı Klemens Yuvası (M4 Pirinç Düz Tornavida Başlı Vidalar) */}
          {[34, 56, 78, 100, 122, 144].map((screwX, sIdx) => (
            <g key={`n-screw-${sIdx}`} transform={`translate(${screwX}, 15)`}>
              {/* Vida Kovanı / Giriş Deliği Yuvası */}
              <circle cx="0" cy="0" r="7.5" fill="#78350f" stroke="#451a03" strokeWidth="0.8" />
              {/* Pirinç Vida Başı */}
              <circle cx="0" cy="0" r="6" fill={`url(#brassScrewGrad-${uid})`} stroke="#78350f" strokeWidth="0.6" />
              {/* Düz Tornavida Yarığı (Slotted Screw Slot) */}
              <line x1="-4" y1="-0.8" x2="4" y2="0.8" stroke="#451a03" strokeWidth="1.8" strokeLinecap="round" />
              {/* Klemens Terminal Numarası (N1..N6) */}
              <text x="0" y="-8.5" fill="#38bdf8" fontSize="6.5" fontWeight="bold" fontFamily="monospace" textAnchor="middle">
                N{sIdx + 1}
              </text>
            </g>
          ))}
        </g>

        {/* --- TOPRAK BARASI (PE EARTH BUSBAR - SARI/YEŞİL İZOLATÖRLÜ BAKIR DİZİ) --- */}
        {/* Konum: Üst Sağ (X = 390, Y = 74) */}
        <g id="earth-busbar-unit" transform="translate(390, 74)">
          {/* Sarı-Yeşil Yan İzolatör Ayakları */}
          <rect x="-8" y="2" width="16" height="26" rx="3" fill={`url(#greenInsulatorGrad-${uid})`} stroke="#3f6212" strokeWidth="1" />
          <rect x="156" y="2" width="16" height="26" rx="3" fill={`url(#greenInsulatorGrad-${uid})`} stroke="#3f6212" strokeWidth="1" />

          {/* Som Bakır Bara Gövdesi */}
          <rect x="4" y="5" width="156" height="20" rx="2" fill={`url(#copperBarGrad-${uid})`} stroke="#92400e" strokeWidth="1.2" />
          <line x1="6" y1="6.5" x2="158" y2="6.5" stroke="#fef9c3" strokeWidth="1" opacity="0.85" />
          <line x1="6" y1="23.5" x2="158" y2="23.5" stroke="#451a03" strokeWidth="0.8" />

          {/* Sol Toprak Sembolü ve Rozeti */}
          <rect x="8" y="7" width="18" height="16" rx="2" fill="#15803d" stroke="#166534" strokeWidth="0.8" />
          <g transform="translate(17, 10)" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round">
            <line x1="0" y1="0" x2="0" y2="4.5" />
            <line x1="-4" y1="4.5" x2="4" y2="4.5" />
            <line x1="-2.5" y1="6.8" x2="2.5" y2="6.8" />
            <line x1="-1" y1="9" x2="1" y2="9" />
          </g>

          {/* 6 Adet Vidalı Klemens Yuvası (PE1..PE6) */}
          {[36, 58, 80, 102, 124, 146].map((screwX, sIdx) => (
            <g key={`pe-screw-${sIdx}`} transform={`translate(${screwX}, 15)`}>
              <circle cx="0" cy="0" r="7.5" fill="#78350f" stroke="#451a03" strokeWidth="0.8" />
              <circle cx="0" cy="0" r="6" fill={`url(#brassScrewGrad-${uid})`} stroke="#78350f" strokeWidth="0.6" />
              <line x1="-4" y1="0.8" x2="4" y2="-0.8" stroke="#451a03" strokeWidth="1.8" strokeLinecap="round" />
              <text x="0" y="-8.5" fill="#bef264" fontSize="6.5" fontWeight="bold" fontFamily="monospace" textAnchor="middle">
                PE{sIdx + 1}
              </text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
};

export default PlasticEnclosureSvg;
